import { useTwilioContext } from './wait_for_twilio';
import CalendarTime         from '../components/calendar_time';
import classnames           from 'classnames';
import InitialsAvatar       from '../components/initials_avatar';
import React                from 'react';


export default function ConversationLink({ businessID, uniqueName, friendlyName, otherParticipantID, className = '', selectedConversationID }) {
  const [ conversation, setConversation ] = React.useState(null);
  const { client }                        = useTwilioContext();
  const { lastMessages }                  = useTwilioContext();
  const lastMessage                       = useLastMessage({ conversation, uniqueName, lastMessages });
  const href                              = `/${businessID}/team-chat/${uniqueName}`;
  const isRoom                            = uniqueName.includes('business:');

  React.useEffect(function() {
    async function getChatRoom() {
      try {
        const fetchedConversation = await client.getChannelByUniqueName(uniqueName);
        setConversation(fetchedConversation);
      } catch (error) {
        if (error.status !== 404)
          throw error;
      }
    }

    getChatRoom();

  }, [ businessID, client, uniqueName ]);

  const itemClassName = classnames(className, 'conversation__item__link item-link item-content', { 'item-link--highlight': uniqueName === selectedConversationID });

  return (
    <div>
      <a href={href} className={itemClassName}>
        <div className="item-media">
          <Avatar isRoom={isRoom} name={friendlyName}/>
          {!isRoom && <OnlineIndicator userID={otherParticipantID}/>}
        </div>
        <div className="item-inner">
          <div className="item-title-row">
            <div className="conversation__item__title item-title truncate">
              {friendlyName}
            </div>
            {lastMessage && (
              <div className="conversation__item__timestamp item-after">
                <CalendarTime time={lastMessage.timestamp}/>
              </div>
            )}
          </div>
          {lastMessage && <LastMessage lastMessage={lastMessage}/>}
        </div>
      </a>
    </div>
  );
}

function OnlineIndicator({ userID }) {
  const { client }                = useTwilioContext();
  const [ isOnline, setIsOnline ] = React.useState(false);
  const [ user, setUser ]         = React.useState(null);

  React.useEffect(function() {
    async function fetchUser() {
      const identity = `user:${userID}`;
      const newUser  = await client.getUser(identity);
      setUser(newUser);
      setIsOnline(newUser.online);
    }

    fetchUser();
  }, [ client, userID ]);

  React.useEffect(function() {
    function onUserUpdate({ user: updatedUser, updateReasons }) {
      if (updateReasons.includes('online'))
        setIsOnline(updatedUser.online);
    }

    if (user)
      user.on('updated', onUserUpdate);

    return function() {
      if (user)
        user.removeListener('updated', onUserUpdate);
    };

  }, [ client, user ]);

  if (isOnline)
    return <div className="online-indicator"/>;
  else
    return null;
}

function LastMessage({ lastMessage }) {
  const { client }                    = useTwilioContext();
  const [ authorName, setAuthorName ] = React.useState(null);

  React.useEffect(function() {
    async function fetchAuthorName() {
      const userDescriptor   = await client.getUserDescriptor(lastMessage.author);
      const { friendlyName } = userDescriptor;
      setAuthorName(friendlyName);
    }

    fetchAuthorName();
  }, [ client, lastMessage.author ]);

  if (!authorName)
    return null;

  return (
    <div className="conversation__item__subtitle item-subtitle item-text truncate">
      {authorName}: {lastMessage.media ? 'media file' : lastMessage.body}
    </div>
  );
}

function useLastMessage({ conversation, uniqueName, lastMessages }) {
  const [ lastMessage, setLastMessage ] = React.useState(null);

  React.useEffect(function() {
    async function getLastMessage() {
      const savedMessage = lastMessages[uniqueName];
      if (savedMessage)
        setLastMessage(savedMessage);
      else if (conversation && lastMessage === null) {
        const { items } = await conversation.getMessages(1);
        const message   = items[0] || '';
        setLastMessage(message);
      }
    }

    getLastMessage();

  }, [ conversation, lastMessage, lastMessages, uniqueName ]);

  return lastMessage;
}

function Avatar({ name, isRoom }) {
  if (isRoom)
    return <i className="icons8 icons8-chat-room"/>;
  else
    return <InitialsAvatar name={name}/>;
}
